import { Component, OnInit, OnDestroy, computed, inject, signal } from '@angular/core';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { Location, CommonModule } from '@angular/common';
import { HeaderService } from '@core/services/header.service';
import { Subject, finalize, takeUntil, filter } from 'rxjs';
import { AuthStore } from '@features/auth/auth.store';
import { AuthService } from '@features/auth/services/auth.service';
import { AvatarComponent } from '@shared/components/avatar/avatar.component';
import { AppPaths } from '@core/routes/app-paths';
import { APP_CONSTANTS } from '@core/constants/app.constants';
import {
  getMainLayoutHomeRouterLink,
  hasFleetAndTeamAccess,
  hasJobsAccess,
  hasMarketplaceAccess,
  hasNonAdminBusinessAccess,
  hasPreferredCompaniesAccess,
  hasTruckingJobsAccess,
  hasTruckingRole,
  hasWorkSessionsReviewAccess,
  isForemanOnlyUser,
} from '@core/utils/role.utils';
import { ComplianceReminderService } from '@core/services/compliance-reminder.service';

// NG-Zorro Imports
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzAlertModule } from 'ng-zorro-antd/alert';

interface IMenuItem {
  label: string;
  route: string;
  icon: string;
}

@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    AvatarComponent,
    NzLayoutModule,
    NzMenuModule,
    NzIconModule,
    NzDropDownModule,
    NzButtonModule,
    NzTypographyModule,
    NzAlertModule,
  ],
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  isCollapsed = signal(false);
  currentTitle = signal('');
  private destroy$ = new Subject<void>();
  public authStore = inject(AuthStore);
  private authService = inject(AuthService);
  private headerService = inject(HeaderService);
  router = inject(Router);
  private location = inject(Location);
  private readonly complianceReminder = inject(ComplianceReminderService);
  readonly AppPaths = AppPaths;

  readonly showComplianceReminderBanner = this.complianceReminder.outstandingRequired;

  // Computed values for role-based navigation
  readonly isAdmin = computed(() => this.authStore.isAdmin());
  /**
   * Platform staff (admin in primaryRole or roles) with no contractor/trucking business access:
   * sidebar shows only Admin → Company Management and restricted routes redirect there.
   * If the same user also has contractor/trucking roles, they get the usual role menus too.
   */
  readonly isPurePlatformStaff = computed(() => {
    if (!this.authStore.isAdmin()) return false;
    const user = this.authStore.user();
    if (!user) return false;
    return !hasNonAdminBusinessAccess({
      primaryRole: user.primaryRole,
      roles: user.roles,
      companyType: user.companyType,
    });
  });

  readonly sidebarHomeRouterLink = computed(() =>
    getMainLayoutHomeRouterLink(this.authStore.user()),
  );
  readonly isDispatcher = computed(() => this.authStore.isDispatcher());
  readonly isForemanOnly = computed(() => {
    const user = this.authStore.user();
    return isForemanOnlyUser({
      primaryRole: user?.primaryRole,
      roles: user?.roles,
      companyType: user?.companyType,
    });
  });

  readonly isTruckingCompanyMember = computed(() => {
    const user = this.authStore.user();
    return hasTruckingRole({
      primaryRole: user?.primaryRole,
      roles: user?.roles,
    });
  });
  readonly userName = computed(() => this.authStore.user()?.name || '');

  // Common (hidden only for pure platform staff with no business roles)
  readonly commonMenuItems = computed<IMenuItem[]>(() => {
    if (this.isPurePlatformStaff()) return [];

    const user = this.authStore.user();
    const payload = {
      primaryRole: user?.primaryRole,
      roles: user?.roles,
      companyType: user?.companyType,
    };

    if (this.isForemanOnly()) {
      return [{ label: 'Work Sessions', route: AppPaths.WORK_SESSIONS, icon: 'audit' }];
    }

    const items: IMenuItem[] = [];
    const showJobs = hasJobsAccess(payload) || hasTruckingJobsAccess(payload);
    if (showJobs) {
      items.push({ label: 'Jobs', route: AppPaths.JOBS, icon: 'tool' });
    }
    const showMarketplace = hasMarketplaceAccess(payload);
    if (showMarketplace) {
      items.push({
        label: 'Marketplace',
        route: AppPaths.MARKETPLACE,
        icon: 'shop',
      });
    }
    const showFleetTeam = hasFleetAndTeamAccess(payload);
    if (showFleetTeam) {
      items.push(
        { label: 'Fleet', route: AppPaths.FLEET, icon: 'truck' },
        { label: 'Team', route: AppPaths.TEAM, icon: 'team' },
      );
      if (hasPreferredCompaniesAccess(payload)) {
        items.push({
          label: 'Preferred Companies',
          route: AppPaths.PREFERRED_COMPANIES,
          icon: 'ordered-list',
        });
      }
    }
    if (hasWorkSessionsReviewAccess(payload)) {
      items.push({
        label: 'Work Sessions',
        route: AppPaths.WORK_SESSIONS,
        icon: 'audit',
      });
    }

    const map = new Map<string, IMenuItem>();
    for (const item of items) {
      if (!map.has(item.route)) map.set(item.route, item);
    }
    return Array.from(map.values());
  });

  // Admin-only section
  readonly adminMenuItems = computed<IMenuItem[]>(() => {
    if (!this.isAdmin()) return [];

    const items: IMenuItem[] = [
      // { label: 'Dashboard', route: AppPaths.DASHBOARD, icon: 'dashboard' },
      {
        label: 'Company Management',
        route: `${AppPaths.ADMIN.ROOT}/${AppPaths.ADMIN.COMPANY_MANAGEMENT}`,
        icon: 'user',
      },
      {
        label: 'Compliance',
        route: `${AppPaths.ADMIN.ROOT}/${AppPaths.ADMIN.COMPLIANCE}`,
        icon: 'safety-certificate',
      },
      // { label: 'Billing and Payments', route: AppPaths.BILLING, icon: 'dollar' },
      // { label: 'Reports and Analytics', route: AppPaths.REPORTS, icon: 'bar-chart' },
      // { label: 'Alerts and Issues', route: AppPaths.ALERTS, icon: 'alert' },
      // {
      //   label: 'Documents Review',
      //   route: `${AppPaths.ADMIN.ROOT}/${AppPaths.ADMIN.DOCUMENT_REVIEW}`,
      //   icon: 'audit',
      // },
    ];

    const map = new Map<string, IMenuItem>();
    for (const item of items) {
      if (!map.has(item.route)) map.set(item.route, item);
    }
    return Array.from(map.values());
  });

  /**
   * Horizontal rule before the Admin block. Hidden for pure platform admin (only `admin` role,
   * no contractor/trucking menu) so the line does not appear below empty space.
   */
  readonly showAdminMenuDivider = computed(
    () => this.commonMenuItems().length > 0 && this.adminMenuItems().length > 0,
  );

  // Backward compatibility: full flat menu (not used in template after grouping)
  readonly menuItems = computed<IMenuItem[]>(() => {
    const all = [...this.commonMenuItems(), ...this.adminMenuItems()];
    const unique = new Map<string, IMenuItem>();
    for (const it of all) if (!unique.has(it.route)) unique.set(it.route, it);
    return Array.from(unique.values());
  });

  readonly avatarFirstName = computed(() => {
    const user = this.authStore.user();
    let result = '';

    if (user?.firstName) {
      result = user.firstName;
    } else if (user?.name?.includes(' ')) {
      result = user.name.split(' ')[0];
    } else if (user?.name) {
      result = user.name;
    } else if (user?.email) {
      // Fallback to email if no name is available
      result = user.email.split('@')[0];
    }

    return result;
  });

  readonly avatarLastName = computed(() => {
    const user = this.authStore.user();
    let result = '';

    if (user?.lastName) {
      result = user.lastName;
    } else if (user?.name?.includes(' ')) {
      result = user.name.split(' ')[1] || '';
    }

    return result;
  });

  /** Routes that platform admin should not access - redirect to Company Management */
  private readonly platformAdminRestrictedPaths = [
    `/${AppPaths.JOBS}`,
    `/${AppPaths.MARKETPLACE}`,
    `/${AppPaths.FLEET}`,
    `/${AppPaths.TEAM}`,
    `/${AppPaths.PREFERRED_COMPANIES}`,
  ];

  private readonly foremanOnlyRestrictedPaths = [
    `/${AppPaths.JOBS}`,
    `/${AppPaths.MARKETPLACE}`,
    `/${AppPaths.FLEET}`,
    `/${AppPaths.TEAM}`,
    `/${AppPaths.PREFERRED_COMPANIES}`,
  ];

  goToProfileDocuments(): void {
    void this.router.navigate([`/${AppPaths.PROFILE}`], {
      queryParams: { tab: 'documents' },
    });
  }

  ngOnInit(): void {
    // Compliance banner: one GET batch per shell load (after login redirect or session restore).
    this.complianceReminder.refreshFromUser(this.authStore.user() ?? null);
    // Load collapsed state from localStorage
    const storedState = localStorage.getItem(APP_CONSTANTS.STORAGE.SIDE_MENU_COLLAPSED);

    if (storedState !== null) {
      try {
        const parsedState = JSON.parse(storedState);
        this.isCollapsed.set(parsedState);
      } catch {
        // If parsing fails, remove the corrupted value
        localStorage.removeItem(APP_CONSTANTS.STORAGE.SIDE_MENU_COLLAPSED);
      }
    }

    // Subscribe to header title changes
    this.headerService.title$.pipe(takeUntil(this.destroy$)).subscribe(title => {
      this.currentTitle.set(title);
    });

    // Redirect platform admin from Jobs/Fleet/Team to Company Management
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        const url = this.router.url.split('?')[0];

        if (this.isForemanOnly()) {
          const workSessionsPath = `/${AppPaths.WORK_SESSIONS}`;
          const isRestricted =
            url === '/' ||
            this.foremanOnlyRestrictedPaths.some(p => url === p || url.startsWith(`${p}/`));
          if (isRestricted && !url.startsWith(workSessionsPath)) {
            this.router.navigate([AppPaths.WORK_SESSIONS], { replaceUrl: true });
          }
          return;
        }

        if (!this.isPurePlatformStaff()) return;
        const managementPath = `/${AppPaths.ADMIN.ROOT}/${AppPaths.ADMIN.COMPANY_MANAGEMENT}`;
        const isRestricted =
          url === '/' ||
          this.platformAdminRestrictedPaths.some(p => url === p || url.startsWith(p + '/'));
        if (isRestricted && !url.startsWith(managementPath)) {
          this.router.navigate([AppPaths.ADMIN.ROOT, AppPaths.ADMIN.COMPANY_MANAGEMENT], {
            replaceUrl: true,
          });
        }
      });
  }

  setCollapsedState(collapsed: boolean): void {
    queueMicrotask(() => {
      this.isCollapsed.set(collapsed);
      const stateToSave = JSON.stringify(collapsed);
      localStorage.setItem(APP_CONSTANTS.STORAGE.SIDE_MENU_COLLAPSED, stateToSave);
    });
  }

  goToProfile(): void {
    this.router.navigate([AppPaths.PROFILE]);
  }

  logout(): void {
    this.complianceReminder.clear();
    // Set sidebar to expanded state by default after logout
    this.setCollapsedState(false);
    this.authService
      .logout()
      .pipe(
        finalize(() => {
          // Always ensure local cleanup
          this.authStore.logout();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          // server logout succeeded; cleanup already handled in finalize
        },
        error: () => {
          // on error, still perform local cleanup (finalize)
        },
      });
  }

  goBack(): void {
    this.location.back();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
